'use client';
import { useCallback, useMemo } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { serializeParams } from '../services/utils';

type SetValue<T> = (
  value: T | ((prevValue: T) => T),
  options?: { replaceHistory?: boolean }
) => void;

export function useQueryParams<T extends string | number | boolean | null>(
  param: string,
  defaultValue: T
): [T, SetValue<T>] {
  const router = useRouter();
  const searchParams = useSearchParams();

  const value = useMemo(() => {
    const paramValue = searchParams.get(param);
    if (paramValue === null) {
      return defaultValue;
    }

    if (typeof defaultValue === 'number') {
      return Number(paramValue) as T;
    }
    if (typeof defaultValue === 'boolean') {
      return (paramValue === 'true') as T;
    }
    return paramValue as T;
  }, [searchParams, param, defaultValue]);

  const setValue: SetValue<T> = useCallback(
    (newValue, options = {}) => {
      const current = new URLSearchParams(Array.from(searchParams.entries()));

      const valueToSet =
        typeof newValue === 'function' ? (newValue as (prevValue: T) => T)(value) : newValue;

      if (valueToSet === null || valueToSet === undefined) {
        current.delete(param);
      } else {
        current.set(param, String(valueToSet));
      }

      // Use serializeParams to ensure proper encoding of spaces as %20
      const search = serializeParams(current);
      const query = search ? `?${search}` : '';

      // Determine whether to use push or replace based on the `replace` option
      if (options?.replaceHistory) {
        router.replace(`${window.location.pathname}${query}`, { scroll: false });
      } else {
        router.push(`${window.location.pathname}${query}`, { scroll: false });
      }
    },
    [router, searchParams, param, value]
  );

  return [value, setValue];
}
